import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import { BannerService } from '../../shared/services/banner.service';
import { HelperService } from '../helper/helper.service';
import * as Models from '../../models/commonModels';
import { DictionaryService } from './dictionary.service';
import * as DictionaryActions from './dictionary.action';
import * as DictionaryModels from '../../models/dictionary/dictionary';
import { CustomerDictionarySearchModel } from '../../models/customer/customerSearch';
import { StorageService } from '../../shared/services/storage.service';


export interface DictionaryStateModel {
  AttachmentType: Models.Response<Models.DictionaryModel[]>;
  AfterSalesPeriod: Models.Response<Models.DictionaryModel[]>;
  AdministratorRoles: Models.Response<Models.DictionaryModel[]>;
  CustomerRoles: Models.Response<Models.DictionaryModel[]>;
  Customers: Models.Response<Models.DictionaryModel[]>;
  InvoiceTypes: Models.Response<Models.DictionaryModel[]>;
  InvoiceStatuses: Models.Response<Models.DictionaryModel[]>;
  ExternalCustomers: Models.Response<Models.DictionaryModel[]>;
  AddressesDictionary: DictionaryModels.AddressesDictionaryModel[];
  Workers: Models.Response<Models.DictionaryModel[]>;
  CustomerInvoiceSeparateByType: Models.Response<Models.DictionaryModel[]>;
  CourierParcelWeights: Models.Response<Models.DictionaryModel[]>;
  CourierParcelValues: Models.Response<Models.DictionaryModel[]>;
  CourierGarageOrderDelayDays: Models.Response<Models.DictionaryModel[]>;
  PaymentTypes: Models.Response<Models.DictionaryModel[]>;
  Garages: Models.Response<Models.DictionaryModel[]>;
  Cars: Models.Response<Models.DictionaryModel[]>;
  CarBrands: Models.Response<Models.DictionaryModel[]>;
  CarModels: Models.Response<Models.DictionaryModel[]>;
  CarTypes: Models.Response<Models.DictionaryModel[]>;
  CostEstimationStatus: Models.Response<Models.DictionaryModel[]>;
  GaragePlaceType: Models.Response<Models.DictionaryModel[]>;
  APIRights: Models.Response<Models.DictionaryModel[]>;
  APIServices: Models.Response<Models.DictionaryModel[]>;
  GarageInvoiceStatuses: Models.Response<Models.DictionaryModel[]>;
  GarageMessageTypes: Models.Response<Models.DictionaryModel[]>;
  EmailTypes: Models.Response<Models.DictionaryModel[]>;
  KsefInvoiceCostStatuses: Models.Response<Models.DictionaryModel[]>;
  KsefInvoiceSaleStatuses: Models.Response<Models.DictionaryModel[]>;
  KsefInvoiceTypes: Models.Response<Models.DictionaryModel[]>;
  KsefVatRates: Models.Response<Models.DictionaryModel[]>;
  ServiceCostTypes: Models.Response<Models.DictionaryModel[]>;
  InvoiceKSeFStatuses: Models.Response<Models.DictionaryModel[]>;
}

class DictionaryKeys {
  public static readonly CustomerRoles: string = "CustomerRoles";
  public static readonly CarBrands: string = "CarBrands";

  public static readonly InvoiceTypes: string = "InvoiceTypes";
  public static readonly InvoiceStatuses: string = "InvoiceStatuses";
  public static readonly InvoiceKSeFStatuses: string = "InvoiceKSeFStatuses";

  public static readonly CourierParcelWeights: string = "CourierParcelWeights";
  public static readonly CourierParcelValues: string = "CourierParcelValues";
  public static readonly CourierGarageOrderDelayDays: string = "CourierGarageOrderDelayDays";

  public static readonly PaymentTypes: string = "PaymentTypes";

  public static readonly CostEstimationStatus: string = "CostEstimationStatus";

  public static readonly APIRights: string = "APIRights";
  public static readonly APIServices: string = "APIServices";

  public static readonly GaragePlaceType: string = "GaragePlaceType";
  public static readonly GarageInvoiceStatuses: string = "GarageInvoiceStatuses";
  public static readonly GarageMessageTypes: string = "GarageMessageTypes";
  public static readonly EmailTypes: string = "EmailTypes";
  public static readonly KsefInvoiceCostStatuses: string = "KsefInvoiceCostStatuses";
  public static readonly KsefInvoiceSaleStatuses: string = "KsefInvoiceSaleStatuses";
  public static readonly ServiceCostTypes: string = "ServiceCostTypes";
  public static readonly KsefInvoiceTypes: string = "KsefInvoiceTypes";
  public static readonly KsefVatRates: string = "KsefVatRates";

}


@State<DictionaryStateModel>({
  name: 'dictionaryState',
  defaults: {
    AttachmentType: null,
    AfterSalesPeriod: null,
    AdministratorRoles: null,
    CustomerRoles: null,
    Customers: null,
    InvoiceTypes: null,
    InvoiceStatuses: null,
    ExternalCustomers: null,
    AddressesDictionary: null,
    Workers: null,
    CustomerInvoiceSeparateByType: null,
    CourierParcelWeights: null,
    CourierParcelValues: null,
    CourierGarageOrderDelayDays: null,
    PaymentTypes: null,
    Garages: null,
    Cars: null,
    CarBrands: null,
    CarModels: null,
    CarTypes: null,
    CostEstimationStatus: null,
    GaragePlaceType: null,
    APIRights: null,
    APIServices: null,
    GarageInvoiceStatuses: null,
    GarageMessageTypes: null,
    EmailTypes: null,
    KsefInvoiceCostStatuses: null,
    KsefInvoiceSaleStatuses: null,
    ServiceCostTypes: null,
    KsefInvoiceTypes: null,
    KsefVatRates: null,
    InvoiceKSeFStatuses: null
  }
})

@Injectable()
export class DictionaryState {

  constructor(
    private helperService: HelperService,
    private dictionaryService: DictionaryService,
    private bannerService: BannerService,
    private storageService: StorageService
  ) { }

  @Selector()
  static AttachmentType(state: DictionaryStateModel) {
    return state.AttachmentType;
  }

  @Selector()
  static AfterSalesPeriod(state: DictionaryStateModel) {
    return state.AfterSalesPeriod;
  }

  @Selector()
  static AdministratorRoles(state: DictionaryStateModel) {
    return state.AdministratorRoles;
  }

  @Selector()
  static Customers(state: DictionaryStateModel) {
    return state.Customers;
  }

  @Selector()
  static CustomerRoles(state: DictionaryStateModel) {
    return state.CustomerRoles;
  }

  @Selector()
  static InvoiceTypes(state: DictionaryStateModel) {
    return state.InvoiceTypes;
  }

  @Selector()
  static InvoiceStatuses(state: DictionaryStateModel) {
    return state.InvoiceStatuses;
  }

  @Selector()
  static ExternalCustomers(state: DictionaryStateModel) {
    return state.ExternalCustomers;
  }

  @Selector()
  static AddressesDictionary(state: DictionaryStateModel) {
    return state.AddressesDictionary;
  }

  @Selector()
  static Workers(state: DictionaryStateModel) {
    return state.Workers;
  }

  @Selector()
  static CustomerInvoiceSeparateByType(state: DictionaryStateModel) {
    return state.CustomerInvoiceSeparateByType;
  }


  @Selector()
  static CourierParcelValues(state: DictionaryStateModel) {
    return state.CourierParcelValues;
  }

  @Selector()
  static CourierParcelWeights(state: DictionaryStateModel) {
    return state.CourierParcelWeights;
  }

  @Selector()
  static CourierGarageOrderDelayDays(state: DictionaryStateModel) {
    return state.CourierGarageOrderDelayDays;
  }

  @Selector()
  static PaymentTypes(state: DictionaryStateModel) {
    return state.PaymentTypes;
  }

  @Selector()
  static Garages(state: DictionaryStateModel) {
    return state.Garages;
  }

  @Selector()
  static CarBrands(state: DictionaryStateModel) {
    return state.CarBrands;
  }

  @Selector()
  static CarModels(state: DictionaryStateModel) {
    return state.CarModels;
  }

  @Selector()
  static CarTypes(state: DictionaryStateModel) {
    return state.CarTypes;
  }

  @Selector()
  static Cars(state: DictionaryStateModel) {
    return state.Cars;
  }

  @Selector()
  static CostEstimationStatus(state: DictionaryStateModel) {
    return state.CostEstimationStatus;
  }

  @Selector()
  static GaragePlaceType(state: DictionaryStateModel) {
    return state.GaragePlaceType;
  }

  @Selector()
  static APIRights(state: DictionaryStateModel) {
    return state.APIRights;
  }

  @Selector()
  static APIServices(state: DictionaryStateModel) {
    return state.APIServices;
  }

  @Selector()
  static GarageInvoiceStatuses(state: DictionaryStateModel) {
    return state.GarageInvoiceStatuses;
  }

  @Selector()
  static GarageMessageTypes(state: DictionaryStateModel) {
    return state.GarageMessageTypes;
  }

  @Selector()
  static EmailTypes(state: DictionaryStateModel) {
    return state.EmailTypes;
  }

  @Selector()
  static KsefInvoiceCostStatuses(state: DictionaryStateModel) {
    return state.KsefInvoiceCostStatuses;
  }

  @Selector()
  static KsefInvoiceSaleStatuses(state: DictionaryStateModel) {
    return state.KsefInvoiceSaleStatuses;
  }

  @Selector()
  static ServiceCostTypes(state: DictionaryStateModel) {
    return state.ServiceCostTypes;
  }

  @Selector()
  static KsefInvoiceTypes(state: DictionaryStateModel) {
    return state.KsefInvoiceTypes;
  }

  @Selector()
  static KsefVatRates(state: DictionaryStateModel) {
    return state.KsefVatRates;
  }

  @Selector()
  static InvoiceKSeFStatuses(state: DictionaryStateModel) {
    return state.InvoiceKSeFStatuses;
  }

  @Action(DictionaryActions.GetAttachmentType)
  GetAttachmentType(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetAttachmentType
  ) {
    context.setState(
      patch({
        AttachmentType: null
      })
    );

    const errorMessage = 'Błąd pobierania typu dokumentów.';

    const state = context.getState();

    if (!state.AttachmentType) {
      this.dictionaryService.getAttachmentType().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              AttachmentType: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetAttachmentType', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetAfterSalesPeriod)
  GetAfterSalesPeriod(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetAfterSalesPeriod
  ) {
    context.setState(
      patch({
        AfterSalesPeriod: null
      })
    );

    const errorMessage = 'Błąd pobierania listy okresów posprzedażnych.';

    this.dictionaryService.getAfterSalesPeriod().subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            AfterSalesPeriod: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetAfterSalesPeriod', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetAdministratorRoles)
  GetAdministratorRoles(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetAdministratorRoles
  ) {
    context.setState(
      patch({
        AdministratorRoles: null
      })
    );

    const errorMessage = 'Błąd pobierania roli administracyjnych.';

    this.dictionaryService.getAdministratorRoles().subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            AdministratorRoles: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetAdministratorRoles', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetCustomers)
  GetCustomers(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCustomers
  ) {
    context.setState(
      patch({
        Customers: null
      })
    );

    const errorMessage = 'Błąd pobierania listy klientów.';
    let onlyMy = action.OnlyMy == undefined ? false : action.OnlyMy;
    let search: CustomerDictionarySearchModel = new CustomerDictionarySearchModel();
    search.OnlyMy = onlyMy;
    search.GarageId = action.GarageId;

    this.dictionaryService.getCustomers(search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Customers: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetCustomers', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetCustomerRoles)
  GetCustomerRoles(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCustomerRoles
  ) {

    const errorMessage = 'Błąd pobierania roli klienta.';

    let storageKey = DictionaryKeys.CustomerRoles;

    context.setState(
      patch({
        CustomerRoles: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.CustomerRoles) {
      this.dictionaryService.getCustomerRoles().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CustomerRoles: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCustomerRoles', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetInvoiceTypes)
  GetInvoiceTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetInvoiceTypes
  ) {

    const errorMessage = 'Błąd pobierania typów faktur.';

    let storageKey = DictionaryKeys.InvoiceTypes;

    context.setState(
      patch({
        InvoiceTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.InvoiceTypes) {
      this.dictionaryService.getInvoiceTypes().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              InvoiceTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetInvoiceTypes', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetInvoiceStatuses)
  GetInvoiceStatuses(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetInvoiceStatuses
  ) {

    const errorMessage = 'Błąd pobierania statusów faktur.';

    let storageKey = DictionaryKeys.InvoiceStatuses;

    context.setState(
      patch({
        InvoiceStatuses: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.InvoiceStatuses) {

      this.dictionaryService.getInvoiceStatuses().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              InvoiceStatuses: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetInvoiceStatuses', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetExternalCustomers)
  GetExternalCustomers(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetExternalCustomers
  ) {
    context.setState(
      patch({
        ExternalCustomers: null
      })
    );

    const errorMessage = 'Błąd pobierania słownika klientów rozszeżonych.';

    this.dictionaryService.getExternalCustomers().subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            ExternalCustomers: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetExternalCustomers', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetAddressesDictionary)
  GetAddressesDictionary(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetAddressesDictionary
  ) {
    context.setState(
      patch({
        AddressesDictionary: null
      })
    );

    const errorMessage = 'Błąd pobierania słownika adresów.';

    this.dictionaryService.getAddressesDictionary().subscribe((ret) => {
      if (ret.Success) {

        ret.Items.forEach(a => a.Label = `${a.Company} ${a.City} ${a.Address}`);

        context.setState(
          patch({
            AddressesDictionary: ret.Items
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetAddressesDictionary', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetWorkers)
  GetWorkers(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetWorkers
  ) {
    context.setState(
      patch({
        Workers: null
      })
    );

    const errorMessage = 'Błąd pobierania słownika pracowników.';

    this.dictionaryService.getWorkers().subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Workers: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetWorkers', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetCustomerInvoiceSeparateByType)
  GetCustomerInvoiceSeparateByType(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCustomerInvoiceSeparateByType
  ) {
    context.setState(
      patch({
        CustomerInvoiceSeparateByType: null
      })
    );

    const errorMessage = 'Błąd pobierania słownika Rozbicie faktury .';

    this.dictionaryService.getCustomerInvoiceSeparateByType().subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            CustomerInvoiceSeparateByType: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetCustomerInvoiceSeparateByType', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetCourierParcelValues)
  GetCourierParcelValues(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetWorkers
  ) {

    const errorMessage = 'Błąd pobierania słownika wartości przesyłek.';

    let storageKey = DictionaryKeys.CourierParcelValues;

    context.setState(
      patch({
        CourierParcelValues: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.CourierParcelValues) {

      this.dictionaryService.getCourierParcelValues().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CourierParcelValues: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCourierParcelValues', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetCourierParcelWeight)
  GetCourierParcelWeight(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetWorkers
  ) {
    const errorMessage = 'Błąd pobierania słownika wag przesyłek.';

    let storageKey = DictionaryKeys.CourierParcelWeights;

    context.setState(
      patch({
        CourierParcelWeights: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.CourierParcelWeights) {
      this.dictionaryService.getCourierParcelWeight().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CourierParcelWeights: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCourierParcelWeight', error);

          this.bannerService.error(errorMessage);
      });
    }
  }

  @Action(DictionaryActions.GetCourierGarageOrderDelayDays)
  GetCourierGarageOrderDelayDays(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCourierGarageOrderDelayDays
  ) {
    const errorMessage = 'Błąd pobierania słownika opóźnień zamówienia kuriera.';

    let storageKey = DictionaryKeys.CourierGarageOrderDelayDays;

    context.setState(
      patch({
        CourierGarageOrderDelayDays: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.CourierGarageOrderDelayDays) {
      this.dictionaryService.getCourierGarageOrderDelayDays().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CourierGarageOrderDelayDays: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCourierGarageOrderDelayDays', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetPaymentTypes)
  GetPaymentTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetWorkers
  ) {

    const errorMessage = 'Błąd pobierania typów płatności.';

    let storageKey = DictionaryKeys.PaymentTypes;

    context.setState(
      patch({
        PaymentTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.PaymentTypes) {

      this.dictionaryService.getPaymentTypes().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              PaymentTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetPaymentTypes', error);

          this.bannerService.error(errorMessage);
        });

    }
  }

  @Action(DictionaryActions.GetGarages)
  GetGarages(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetGarages
  ) {
    context.setState(
      patch({
        Garages: null
      })
    );

    const errorMessage = 'Błąd pobierania listy Serwisów.';

    this.dictionaryService.getGarages().subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Garages: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetGarages', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetCars)
  GetCars(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCars
  ) {
    context.setState(
      patch({
        Cars: null
      })
    );

    const errorMessage = 'Błąd pobierania listy Pojazdów.';

    this.dictionaryService.getCars(action.RegistrationNumber).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            Cars: ret
          })
        );
      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('DictionaryActions.GetCars', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(DictionaryActions.GetCarBrands)
  GetCarBrands(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCarBrands
  ) {

    const errorMessage = 'Błąd pobierania listy marek samochodów.';

    let storageKey = DictionaryKeys.CarBrands;

    context.setState(
      patch({
        CarBrands: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();


    if (!state.CarBrands) {
      this.dictionaryService.getCarBrands().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CarBrands: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCarBrands', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetCarModels)
  GetCarModels(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCarModels
  ) {
    context.setState(
      patch({
        CarModels: null
      })
    );

    const errorMessage = 'Błąd pobierania listy modeli samochodów.';
    if (action.CarBrandlId)
      this.dictionaryService.getCarModels(action.CarBrandlId).subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CarModels: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCarModels', error);

          this.bannerService.error(errorMessage);
        });
  }

  @Action(DictionaryActions.GetCarTypes)
  GetCarTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCarTypes
  ) {
    context.setState(
      patch({
        CarTypes: null
      })
    );

    const errorMessage = 'Błąd pobierania listy typów samochodów.';
    if (action.CarModelId)
      this.dictionaryService.getCarTypes(action.CarModelId).subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              CarTypes: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCarTypes', error);

          this.bannerService.error(errorMessage);
        });
  }

  @Action(DictionaryActions.GetCostEstimationStatus)
  GetCostEstimationStatus(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetCostEstimationStatus
  ) {

    const errorMessage = 'Błąd pobierania listy marek samochodów.';

    let storageKey = DictionaryKeys.CostEstimationStatus;

    context.setState(
      patch({
        CostEstimationStatus: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.CostEstimationStatus) {
      this.dictionaryService.getCostEstimationStatus().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              CostEstimationStatus: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetCostEstimationStatus', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetGaragePlaceType)
  GaragePlaceType(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetGaragePlaceType
  ) {

    const errorMessage = 'Błąd pobierania typów stanowisk.';

    let storageKey = DictionaryKeys.GaragePlaceType;

    context.setState(
      patch({
        GaragePlaceType: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.GaragePlaceType) {

      this.dictionaryService.getGaragePlaceType().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              GaragePlaceType: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetGaragePlaceType', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetAPIRights)
  APIRights(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetAPIRights
  ) {

    const errorMessage = 'Błąd pobierania uprawnień API.';

    let storageKey = DictionaryKeys.APIRights;

    context.setState(
      patch({
        APIRights: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.APIRights) {
      this.dictionaryService.getAPIRights().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              APIRights: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetAPIRights', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetAPIServices)
  APIServices(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetAPIServices
  ) {

    const errorMessage = 'Błąd pobierania serwisów API.';

    let storageKey = DictionaryKeys.APIServices;

    context.setState(
      patch({
        APIServices: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.APIServices) {

      this.dictionaryService.getAPIServices().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              APIServices: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetAPIServices', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetGarageInvoiceStatuses)
  GetGarageInvoiceStatuses(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetGarageInvoiceStatuses
  ) {

    const errorMessage = 'Błąd pobierania statusów faktur od serwisów.';

    let storageKey = DictionaryKeys.GarageInvoiceStatuses;

    context.setState(
      patch({
        GarageInvoiceStatuses: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.GarageInvoiceStatuses) {
      this.dictionaryService.getGarageInvoiceStatuses().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              GarageInvoiceStatuses: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetGarageInvoiceStatuses', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetMessageTypes)
  GetMessageTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetMessageTypes
  ) {
    const errorMessage = 'Błąd pobierania typów wiadomości.';

    let storageKey = DictionaryKeys.GarageMessageTypes;

    context.setState(
      patch({
        GarageMessageTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.GarageMessageTypes) {
      this.dictionaryService.GetMessageTypes().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              GarageMessageTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetMessageTypes', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetEmailTypes)
  GetEmailTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetEmailTypes
  ) {
    const errorMessage = 'Błąd pobierania typów powiadomień E-mail.';

    let storageKey = DictionaryKeys.EmailTypes;

    context.setState(
      patch({
        EmailTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.EmailTypes) {
      this.dictionaryService.GetEmailTypes().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              EmailTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetEmailTypes', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetKsefInvoiceCostStatuses)
  GetKsefInvoiceCostStatuses(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetInvoiceStatuses
  ) {

    const errorMessage = 'Błąd pobierania statusów faktur KSeF.';

    let storageKey = DictionaryKeys.KsefInvoiceCostStatuses;

    context.setState(
      patch({
        KsefInvoiceCostStatuses: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.KsefInvoiceCostStatuses) {

      this.dictionaryService.GetKsefInvoiceCostStatuses().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              KsefInvoiceCostStatuses: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetKsefInvoiceCostStatuses', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetKSeFInvoiceTypes)
  GetKsefInvoiceTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetKSeFInvoiceTypes
  ) {

    const errorMessage = 'Błąd pobierania typów faktur KSeF.';

    let storageKey = DictionaryKeys.KsefInvoiceTypes;

    context.setState(
      patch({
        KsefInvoiceTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.KsefInvoiceTypes) {

      this.dictionaryService.GetKSeFInvoiceTypes().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              KsefInvoiceTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetKSeFInvoiceTypes', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetServiceCostTypes)
  GetServiceCostTypes(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetServiceCostTypes
  ) {

    const errorMessage = 'Błąd pobierania typów kosztów usług.';

    let storageKey = DictionaryKeys.ServiceCostTypes;

    context.setState(
      patch({
        ServiceCostTypes: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.ServiceCostTypes) {

      this.dictionaryService.GetServiceCostTypes().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              ServiceCostTypes: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetServiceCostTypes', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetKSeFVatRates)
  GetKSeFVatRates(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetKSeFVatRates
  ) {

    const errorMessage = 'Błąd pobierania stawek VAT KSeF.';

    let storageKey = DictionaryKeys.KsefVatRates;

    context.setState(
      patch({
        KsefVatRates: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.KsefVatRates) {

      this.dictionaryService.GetKSeFVatRates().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              KsefVatRates: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetKSeFVatRates', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetKsefInvoiceSaleStatuses)
  GetKsefInvoiceSaleStatuses(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetKsefInvoiceSaleStatuses
  ) {

    const errorMessage = 'Błąd pobierania statusów faktur sprzedażowych KSeF.';

    let storageKey = DictionaryKeys.KsefInvoiceSaleStatuses;

    context.setState(
      patch({
        KsefInvoiceSaleStatuses: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.KsefInvoiceSaleStatuses) {
      this.dictionaryService.GetKsefInvoiceSaleStatuses().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              KsefInvoiceSaleStatuses: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetKsefInvoiceSaleStatuses', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(DictionaryActions.GetInvoiceKSeFStatuses)
  GetInvoiceKSeFStatuses(
    context: StateContext<DictionaryStateModel>,
    action: DictionaryActions.GetInvoiceKSeFStatuses
  ) {
    const errorMessage = 'Błąd pobierania statusów KSeF faktur.';

    let storageKey = DictionaryKeys.InvoiceKSeFStatuses;

    context.setState(
      patch({
        InvoiceKSeFStatuses: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.InvoiceKSeFStatuses) {
      this.dictionaryService.GetInvoiceKSeFStatuses().subscribe((ret) => {
        if (ret.Success) {
          context.setState(
            patch({
              InvoiceKSeFStatuses: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('DictionaryActions.GetInvoiceKSeFStatuses', error);

          this.bannerService.error(errorMessage);
        });
    }
  }
}

